import { useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { useLazyGetClinicianSsoQuery } from '../store/api';

/**
 * Launch DoseSpot in a new tab using the clinician SSO URL.
 *
 * Opens `about:blank` synchronously inside the click handler so the popup
 * blocker doesn't intervene, then redirects to the SSO URL once it resolves.
 * Surfaces a snackbar on popup-block or SSO failure.
 *
 * Callers should track their own analytics around this call — the hook stays
 * source-agnostic so it can be reused by setup CTAs and the notifications bell
 * without conflating funnels.
 */
export function useOpenDoseSpot() {
  const { enqueueSnackbar } = useSnackbar();
  const [getClinicianSso, { isLoading }] = useLazyGetClinicianSsoQuery();

  const openDoseSpot = useCallback(async (): Promise<boolean> => {
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', {
        variant: 'warning',
      });
      return false;
    }
    try {
      const result = await getClinicianSso().unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
        return true;
      }
      ssoWindow.close();
      return false;
    } catch (err: unknown) {
      ssoWindow.close();
      const errorMessage =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data?: { message?: string } }).data?.message ||
            'Failed to open DoseSpot. Please try again.'
          : 'Failed to open DoseSpot. Please try again.';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      return false;
    }
  }, [getClinicianSso, enqueueSnackbar]);

  return { openDoseSpot, isLoading };
}
